/**
 * WorkoutCalendar - Calendar grid view for scheduled workouts
 * Supports monthly, quarterly (3 months), and yearly views
 * Mobile-first responsive design
 * Shows scheduled_workouts (appointments) - click to see details and linked workouts
 *
 * Refactored: Touch-friendly targets (44px min), mobile improvements
 */

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfYear,
  eachDayOfInterval,
  eachMonthOfInterval,
  addMonths,
  format,
  isSameMonth,
  isToday,
  isFuture,
  getMonth,
} from 'date-fns';
import { tr } from 'date-fns/locale';
import { Calendar, Clock, User } from 'lucide-react';
import { useFitnessStore, MUSCLE_GROUPS } from '../../stores/fitnessStore';
import WeeklyTimeline from './WeeklyTimeline';
import DailyTimeline from './DailyTimeline';

// Exercise type config for scheduled workouts
const TYPE_CONFIG = {
  fitness: { label: 'Fitness', emoji: '🏋️', color: 'bg-blue-500', textColor: 'text-blue-400' },
  kickbox: { label: 'Kickbox', emoji: '🥊', color: 'bg-red-500', textColor: 'text-red-400' },
  cardio: { label: 'Kardiyo', emoji: '🏃', color: 'bg-green-500', textColor: 'text-green-400' },
};

const WEEKDAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const WEEKDAYS_SHORT = ['P', 'S', 'Ç', 'P', 'C', 'C', 'P'];
const WEEKDAYS_MOBILE = ['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pa'];

const WorkoutCalendar = () => {
  const { calendarViewMode } = useFitnessStore();

  if (calendarViewMode === 'yearly') {
    return <YearlyCalendar />;
  }

  if (calendarViewMode === 'quarterly') {
    return <QuarterlyCalendar />;
  }

  if (calendarViewMode === 'weekly') {
    return <WeeklyTimeline />;
  }

  if (calendarViewMode === 'daily') {
    return <DailyTimeline />;
  }

  return <MonthlyCalendar />;
};

// =====================================================
// MONTHLY CALENDAR VIEW
// =====================================================

const MonthlyCalendar = () => {
  const {
    currentMonth,
    scheduledWorkouts,
    selectScheduledWorkoutForEdit
  } = useFitnessStore();

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  // Group scheduled workouts by date
  const schedulesByDate = useMemo(() => {
    const map = {};
    scheduledWorkouts.forEach((schedule) => {
      const dateKey = schedule.scheduled_date;
      if (!map[dateKey]) {
        map[dateKey] = [];
      }
      map[dateKey].push(schedule);
    });
    return map;
  }, [scheduledWorkouts]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden"
    >
      {/* Weekday headers - responsive text */}
      <div className="grid grid-cols-7 bg-gray-800/50">
        {WEEKDAYS.map((day, idx) => (
          <div
            key={day}
            className="py-2 sm:py-3 text-center text-xs sm:text-sm font-medium text-gray-400"
          >
            <span className="hidden sm:inline">{day}</span>
            <span className="sm:hidden">{WEEKDAYS_MOBILE[idx]}</span>
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {calendarDays.map((day) => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const daySchedules = schedulesByDate[dateStr] || [];
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isTodayDate = isToday(day);

          return (
            <ScheduleCalendarDay
              key={dateStr}
              day={day}
              schedules={daySchedules}
              isCurrentMonth={isCurrentMonth}
              isToday={isTodayDate}
              onScheduleClick={(schedule) => {
                selectScheduledWorkoutForEdit(schedule);
              }}
            />
          );
        })}
      </div>
    </motion.div>
  );
};

// Schedule Calendar Day - Shows scheduled workouts (appointments)
const ScheduleCalendarDay = ({
  day,
  schedules,
  isCurrentMonth,
  isToday,
  onScheduleClick,
}) => {
  const dayNumber = format(day, 'd');
  const scheduleCount = schedules.length;

  // Status colors
  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-500',
      completed: 'bg-green-500',
      missed: 'bg-red-500',
      cancelled: 'bg-gray-500',
    };
    return colors[status] || colors.scheduled;
  };

  return (
    <div
      className={`
        relative min-h-[72px] sm:min-h-[100px] md:min-h-[120px] p-1.5 sm:p-2 border-r border-b border-gray-800
        ${!isCurrentMonth ? 'opacity-30' : ''}
        ${isToday ? 'ring-2 ring-orange-500 ring-inset' : ''}
        ${scheduleCount > 0 ? 'bg-blue-500/5' : ''}
      `}
    >
      {/* Day number */}
      <div
        className={`
          text-xs sm:text-sm font-medium mb-1 sm:mb-2
          ${isToday ? 'text-orange-400' : 'text-gray-300'}
        `}
      >
        {dayNumber}
      </div>

      {/* Schedule indicators */}
      {scheduleCount > 0 && (
        <div className="space-y-0.5 sm:space-y-1">
          {/* Desktop: show schedule details */}
          {schedules.slice(0, 2).map((schedule) => {
            const typeConfig = TYPE_CONFIG[schedule.exercise_type] || TYPE_CONFIG.fitness;
            const muscleLabel = schedule.muscle_group && MUSCLE_GROUPS[schedule.muscle_group]?.label;
            const clientDisplayName = schedule.client_name || schedule.client_email || '';
            const hasClientInfo = !!clientDisplayName.trim();
            const shortClientName = clientDisplayName ? clientDisplayName.split(' ')[0].split('@')[0] : '';

            return (
              <div
                key={schedule.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onScheduleClick(schedule);
                }}
                className={`
                  w-full hidden sm:flex items-center gap-1 px-1.5 py-0.5 rounded text-xs
                  ${typeConfig.color}/20 hover:${typeConfig.color}/30 transition-colors
                `}
              >
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getStatusColor(schedule.status)}`} />
                <span className={`truncate text-2xs sm:text-xs ${typeConfig.textColor}`}>
                  {hasClientInfo ? shortClientName : ''} {schedule.scheduled_time?.substring(0, 5)}
                </span>
              </div>
            );
          })}

          {/* Mobile: show colored dots - larger for touch */}
          <div className="flex sm:hidden flex-wrap gap-1">
            {schedules.slice(0, 4).map((schedule) => (
              <button
                key={schedule.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onScheduleClick(schedule);
                }}
                className={`w-3 h-3 rounded-full ${getStatusColor(schedule.status)} active:scale-110 transition-transform`}
                aria-label={`Randevu: ${schedule.scheduled_time}`}
              />
            ))}
            {scheduleCount > 4 && (
              <span className="text-2xs text-gray-400 ml-0.5">+{scheduleCount - 4}</span>
            )}
          </div>

          {/* Desktop: show +more */}
          {scheduleCount > 2 && (
            <div className="hidden sm:block text-2xs sm:text-xs text-gray-400 pl-1">
              +{scheduleCount - 2} daha
            </div>
          )}
        </div>
      )}

      {/* Schedule count badge */}
      {scheduleCount > 0 && (
        <div className="absolute top-1 sm:top-2 right-1 sm:right-2 flex items-center gap-0.5 sm:gap-1">
          <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-blue-400" />
          <span className="text-2xs sm:text-xs text-blue-400 font-medium">{scheduleCount}</span>
        </div>
      )}
    </div>
  );
};

// =====================================================
// QUARTERLY CALENDAR VIEW (3 months)
// =====================================================

const QuarterlyCalendar = () => {
  const {
    currentMonth,
    scheduledWorkouts,
    openScheduleWorkoutModal,
    selectScheduledWorkoutForEdit
  } = useFitnessStore();

  // Get 3 months starting from currentMonth
  const months = useMemo(() => {
    return [
      currentMonth,
      addMonths(currentMonth, 1),
      addMonths(currentMonth, 2),
    ];
  }, [currentMonth]);

  // Group scheduled workouts by date
  const schedulesByDate = useMemo(() => {
    const map = {};
    scheduledWorkouts.forEach((schedule) => {
      const dateKey = schedule.scheduled_date;
      if (!map[dateKey]) {
        map[dateKey] = [];
      }
      map[dateKey].push(schedule);
    });
    return map;
  }, [scheduledWorkouts]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
    >
      {months.map((month) => (
        <MiniScheduleCalendar
          key={format(month, 'yyyy-MM')}
          month={month}
          schedulesByDate={schedulesByDate}
          onDayClick={(dateStr) => {
            openScheduleWorkoutModal(dateStr, '10:00');
          }}
          onScheduleClick={(schedule) => {
            selectScheduledWorkoutForEdit(schedule);
          }}
        />
      ))}
    </motion.div>
  );
};

// =====================================================
// YEARLY CALENDAR VIEW
// =====================================================

const YearlyCalendar = () => {
  const {
    currentMonth,
    scheduledWorkouts,
    openScheduleWorkoutModal,
    selectScheduledWorkoutForEdit
  } = useFitnessStore();

  // Get all 12 months of the year
  const year = currentMonth.getFullYear();
  const months = useMemo(() => {
    const yearStart = startOfYear(currentMonth);
    return eachMonthOfInterval({
      start: yearStart,
      end: new Date(year, 11, 1),
    });
  }, [currentMonth, year]);

  // Group scheduled workouts by date
  const schedulesByDate = useMemo(() => {
    const map = {};
    scheduledWorkouts.forEach((schedule) => {
      const dateKey = schedule.scheduled_date;
      if (!map[dateKey]) {
        map[dateKey] = [];
      }
      map[dateKey].push(schedule);
    });
    return map;
  }, [scheduledWorkouts]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="grid grid-cols-2 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4"
    >
      {months.map((month) => (
        <MiniScheduleCalendar
          key={format(month, 'yyyy-MM')}
          month={month}
          schedulesByDate={schedulesByDate}
          compact
          onDayClick={(dateStr) => {
            openScheduleWorkoutModal(dateStr, '10:00');
          }}
          onScheduleClick={(schedule) => {
            selectScheduledWorkoutForEdit(schedule);
          }}
        />
      ))}
    </motion.div>
  );
};

// =====================================================
// MINI SCHEDULE CALENDAR (used in quarterly and yearly views)
// =====================================================

const MiniScheduleCalendar = ({ month, schedulesByDate, compact = false, onDayClick, onScheduleClick }) => {
  // Generate calendar days for this month - always 6 weeks (42 days) for consistent height
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(month);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });

    // Always generate exactly 42 days (6 weeks) for consistent card heights
    const days = [];
    for (let i = 0; i < 42; i++) {
      const day = new Date(calendarStart);
      day.setDate(calendarStart.getDate() + i);
      days.push(day);
    }
    return days;
  }, [month]);

  const monthLabel = format(month, 'MMMM', { locale: tr });

  // Calculate stats
  const { scheduleDays, totalSchedules } = useMemo(() => {
    const monthDates = Object.keys(schedulesByDate).filter(date =>
      date.startsWith(format(month, 'yyyy-MM'))
    );
    return {
      scheduleDays: monthDates.length,
      totalSchedules: monthDates.reduce((sum, date) => sum + schedulesByDate[date].length, 0),
    };
  }, [schedulesByDate, month]);

  // Status colors
  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-500',
      completed: 'bg-green-500',
      missed: 'bg-red-500',
      cancelled: 'bg-gray-500',
    };
    return colors[status] || colors.scheduled;
  };

  return (
    <div className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden">
      {/* Month header */}
      <div className="px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-800/50 text-center">
        <span className="text-xs sm:text-sm font-semibold text-white capitalize">{monthLabel}</span>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 px-0.5 sm:px-1">
        {WEEKDAYS_SHORT.map((day, idx) => (
          <div
            key={`${day}-${idx}`}
            className={`py-0.5 sm:py-1 text-center font-medium text-gray-500 ${compact ? 'text-2xs' : 'text-2xs sm:text-xs'}`}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid - touch-friendly cells */}
      <div className="grid grid-cols-7 px-0.5 sm:px-1 pb-0.5 sm:pb-1 gap-0.5">
        {calendarDays.map((day) => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const daySchedules = schedulesByDate[dateStr] || [];
          const isCurrentMonth = isSameMonth(day, month);
          const isTodayDate = isToday(day);
          const hasSchedules = daySchedules.length > 0;

          return (
            <button
              key={dateStr}
              onClick={() => {
                if (isCurrentMonth) {
                  if (hasSchedules && daySchedules.length === 1) {
                    onScheduleClick(daySchedules[0]);
                  } else {
                    onDayClick(dateStr);
                  }
                }
              }}
              disabled={!isCurrentMonth}
              className={`
                relative aspect-square flex items-center justify-center touch-manipulation
                ${compact ? 'text-2xs min-w-[20px] min-h-[20px]' : 'text-2xs sm:text-xs min-w-[24px] min-h-[24px]'}
                ${!isCurrentMonth ? 'opacity-20 cursor-default' : ''}
                ${isCurrentMonth ? 'cursor-pointer hover:bg-gray-800/50 active:bg-gray-700/50 active:scale-95' : ''}
                ${isTodayDate ? 'ring-1 ring-orange-500 rounded-md' : ''}
                ${hasSchedules && isCurrentMonth ? 'font-bold' : 'font-normal'}
                rounded-md transition-all
              `}
            >
              <span className={`
                ${isTodayDate ? 'text-orange-400' : isCurrentMonth ? 'text-gray-300' : 'text-gray-600'}
              `}>
                {format(day, 'd')}
              </span>

              {/* Schedule indicator dot */}
              {hasSchedules && isCurrentMonth && (
                <div className={`absolute ${compact ? '-bottom-0.5 sm:bottom-0' : 'bottom-0 sm:bottom-0.5'} left-1/2 -translate-x-1/2`}>
                  <div
                    className={`${compact ? 'w-0.5 h-0.5 sm:w-1 sm:h-1' : 'w-1 h-1 sm:w-1.5 sm:h-1.5'} rounded-full ${getStatusColor(daySchedules[0]?.status)}`}
                    title={`${daySchedules.length} randevu`}
                  />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Summary */}
      <div className="px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-800/30 border-t border-gray-800">
        <div className="flex items-center justify-between text-2xs sm:text-xs">
          <div className="flex items-center gap-2 sm:gap-3">
            <div>
              <span className="text-gray-500">Gün: </span>
              <span className="text-blue-400 font-medium">{scheduleDays}</span>
            </div>
            <div>
              <span className="text-gray-500 hidden xs:inline">Randevu: </span>
              <span className="text-gray-500 xs:hidden">Rnd: </span>
              <span className="text-blue-400 font-medium">{totalSchedules}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkoutCalendar;
